import { datadogRum } from '@datadog/browser-rum'
import { DESKTOP_BREAKPOINT } from '@xfers/design-system'
import cookie from 'js-cookie'
import Moment from 'moment'
import posthog from 'posthog-js'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ConfigManager } from '~/managers/ConfigManager'
import FeatureGatingManager from '~/managers/FeatureGatingManager'
import SSOManager from '~/managers/SSOManager'
import { PATHS } from '~/routes/paths'
import { fetchAssetList } from '~/store/assets/actions'
import { selectDgwAccountByCurrency, selectIsAccountListInitiated } from '~/store/assets/selectors'
import { fetchBankList } from '~/store/bankAccounts/actions'
import {
  fetchBlockchainAddresses,
  setBlockchainAddressList,
} from '~/store/blockchainAddresses/actions'
import {
  selectBlockchainAddressList,
  selectIsBlockchainAddressesInitiated,
} from '~/store/blockchainAddresses/selectors'
import {
  selectIsDpt2TncEnabled,
  selectIsOtcTrxHistoryEnabled,
  selectIsTypeformSurveyVisible,
} from '~/store/flippers/selectors'
import { selectIsCkaEnabled } from '~/store/landing/selectors'
import { fetchLatestTransactions } from '~/store/transactions/actions'
import { selectLatestTransactions } from '~/store/transactions/selector'
import {
  selectIsUserFullyVerified,
  selectUserRequiresManualPeriodicReview,
} from '~/store/userInfo/selectors'
import { CURRENCY, PERSONAL_VERIFICATION_STATUS } from '~/types'
import { setIsCkaEnabled } from './store/landingPage/actions'
import { CkaTransactionRestrictionDate } from './store/landingPage/const'
import { init, setIsLockedFeatureOn, setIsTypeformSurveyRequired } from './store/route/actions'
import { getTotalTravelRuleComplianceBlockerCount } from './store/transactions/actions'
import { RootState } from './store/types'
import { fetchUserInfo } from './store/userInfo/actions'
import {
  selectIsDpt2TncAccepted,
  selectIsKillSwitchActive,
  selectUserInfo,
  selectUserVerificationStatus,
} from './store/userInfo/selectors'

function parseNumberFromMediaQuery(mediaQuery: string): number {
  const match = mediaQuery.match(/(\d+(\.\d+)?)/)

  if (match && match[1]) {
    // Convert the extracted string to a floating-point number
    return parseFloat(match[1])
  }

  return NaN
}

export const useInitRoot = () => {
  const desktopBreakpointWidth = parseNumberFromMediaQuery(DESKTOP_BREAKPOINT)
  const location = useLocation()
  const dispatch = useDispatch()
  const {
    user,
    isAccountEnabled,
    isLockedFeatureOn,
    isTypeformSurveyRequired,
    isUserInfoInitialised,
  } = useSelector((state: RootState) => state.route)
  const isAccountListInitiated = useSelector(selectIsAccountListInitiated)
  const isCkaEnabled = useSelector(selectIsCkaEnabled)
  const fetchedNotifications = useSelector((state: RootState) => state.route.notifications)
  const { isTourRunning } = useSelector((state: RootState) => state.tour)
  const latestTransactions = useSelector(selectLatestTransactions)
  const isOtcTrxHistoryEnabled = useSelector(selectIsOtcTrxHistoryEnabled)
  const sgdDgwAccount = useSelector(selectDgwAccountByCurrency)(CURRENCY.XSGD)
  const verificationStatus = useSelector(selectUserVerificationStatus)
  const isBlockchainAddressesInitiated = useSelector(selectIsBlockchainAddressesInitiated)
  const blockchainAddresses = useSelector(selectBlockchainAddressList)
  const { accountLocked, ckaPassed, userId } = useSelector(selectUserInfo)
  const isUserFullyVerified = useSelector(selectIsUserFullyVerified)
  const requiresManualPeriodicReview = useSelector(selectUserRequiresManualPeriodicReview)
  const isKillSwitchActive = useSelector(selectIsKillSwitchActive)
  const isDpt2TncAccepted = useSelector(selectIsDpt2TncAccepted)
  const issDpt2TncEnabled = useSelector(selectIsDpt2TncEnabled)
  const isTypeformSurveyVisible = useSelector(selectIsTypeformSurveyVisible)

  const displayGetStartedTab = useMemo(() => {
    return verificationStatus && verificationStatus !== PERSONAL_VERIFICATION_STATUS.VERIFIED
  }, [isBlockchainAddressesInitiated, blockchainAddresses, verificationStatus])

  const displayMintTab = useMemo(() => {
    return (
      isUserFullyVerified &&
      (!isCkaEnabled || ckaPassed || Moment().isBefore(CkaTransactionRestrictionDate))
    )
  }, [isUserFullyVerified, isCkaEnabled, ckaPassed])

  const shouldRenderUserInfoBar = useMemo(() => {
    return (
      isLockedFeatureOn === false ||
      (isLockedFeatureOn === true && !(accountLocked && requiresManualPeriodicReview))
    )
  }, [isLockedFeatureOn, accountLocked, requiresManualPeriodicReview])

  const notifications = useMemo(
    () => ({
      isLoading: fetchedNotifications.isLoading,
      data: fetchedNotifications.data,
      unreadCount: fetchedNotifications.unreadCount,
    }),
    [fetchedNotifications.isLoading]
  )

  const isDesktopView = useMemo(() => {
    return document.documentElement.clientWidth >= desktopBreakpointWidth
  }, [document.documentElement.clientWidth])

  const isPeriodicReviewPage = useMemo(() => {
    return location.pathname === PATHS.PERIODIC_REVIEW
  }, [location.pathname])

  // required for tour to work properly; prevents rendering antd Menu component twice
  const shouldRenderDesktopViewNavbar = useMemo(() => {
    return (
      (isDesktopView || !isTourRunning) &&
      (isLockedFeatureOn === false ||
        (isLockedFeatureOn === true && !(accountLocked && requiresManualPeriodicReview))) &&
      !isPeriodicReviewPage
    )
  }, [
    isDesktopView,
    isTourRunning,
    isLockedFeatureOn,
    accountLocked,
    requiresManualPeriodicReview,
    isPeriodicReviewPage,
  ])

  const shouldRenderDpt2Tnc = useMemo(() => {
    return !isDpt2TncAccepted && isUserInfoInitialised && !isKillSwitchActive && issDpt2TncEnabled
  }, [isDpt2TncAccepted, isUserInfoInitialised, isKillSwitchActive, issDpt2TncEnabled])

  const shouldRenderTypeformSurvey = useMemo(() => {
    return isTypeformSurveyVisible && isTypeformSurveyRequired
  }, [isTypeformSurveyVisible, isTypeformSurveyRequired])

  useEffect(() => {
    dispatch(init())
    dispatch(fetchBankList())
    dispatch(fetchUserInfo())
    dispatch(getTotalTravelRuleComplianceBlockerCount())
    dispatch(fetchAssetList())

    // double check the lock feature
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    FeatureGatingManager.isEnabled('straitsx_locked_page').then(resp => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      dispatch(setIsLockedFeatureOn(resp))
    })
  }, [])

  useEffect(() => {
    if (!isAccountEnabled) {
      cookie.remove('SESSIONID')
      SSOManager.ssoLogout()
    }
  }, [isAccountEnabled])

  useEffect(() => {
    if (!isAccountListInitiated || !isOtcTrxHistoryEnabled) {
      return
    }

    dispatch(fetchLatestTransactions())
  }, [isAccountListInitiated, isOtcTrxHistoryEnabled])

  useEffect(() => {
    if (!isAccountListInitiated) return

    if (sgdDgwAccount?.accountId) {
      dispatch(fetchBlockchainAddresses(sgdDgwAccount.accountId))
    } else {
      dispatch(setBlockchainAddressList([]))
    }
  }, [isAccountListInitiated, sgdDgwAccount?.accountId])

  useEffect(() => {
    if (latestTransactions && latestTransactions.length > 0) {
      const latestTransactionDate = new Date(latestTransactions[0].createdAt)
      const thresholdDate = new Date()
      thresholdDate.setDate(thresholdDate.getDate() - 90)

      dispatch(
        setIsTypeformSurveyRequired(latestTransactionDate.getTime() >= thresholdDate.getTime())
      )
    }
  }, [latestTransactions?.length])

  useEffect(() => {
    if (ConfigManager.isProd() && userId) {
      datadogRum.setUser({
        straitsx_user_id: userId,
      })
    }
  }, [userId])

  useEffect(() => {
    if (ConfigManager.isProd() && userId && user?.kcId) {
      posthog.identify(posthog.get_distinct_id(), { straitsx_user_id: userId, kcId: user?.kcId })
    }
  }, [userId, user?.kcId])

  // New flippers should go here, check: https://www.notion.so/fazzfinancialgroup/StraitsX-Flipper-Usage-Guideline-08e54bedc7ed4d14b57d8b517ed00425
  useEffect(() => {
    if (user?.kcId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      FeatureGatingManager.isEnabled(
        ['straitsx_dashboard_cka'],
        `${ConfigManager.getEnvironmentMode()}_${ConfigManager.getSiteType()}_${user.kcId}`
      ).then(resp => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        dispatch(setIsCkaEnabled(resp.straitsx_dashboard_cka))
      })
    }
  }, [user?.kcId])

  return {
    displayGetStartedTab,
    displayMintTab,
    isKillSwitchActive,
    isPeriodicReviewPage,
    notifications,
    shouldRenderDesktopViewNavbar,
    shouldRenderDpt2Tnc,
    shouldRenderTypeformSurvey,
    shouldRenderUserInfoBar,
  }
}
